import {createSignal} from './signal';
import type {Detach, ExtractSignalsState, Signal} from './types';

/**
 * Emits the latest values when any source changes. Trace records identify the
 * sources rather than repeating their payloads, which each source already logs.
 * Observer delivery retains the complete values and their identities.
 *
 * @example
 * const latest = combine(audioStats, videoStats);
 */
export const combine = <T extends Signal<unknown>[]>(...signals: T) => {
    const latest = Array.from(
        signals,
        _ => undefined,
    ) as ExtractSignalsState<T>;

    let detachCombinedSignals: Detach[] = [];

    const attach = () => {
        detachCombinedSignals = signals.map((signal, i) =>
            signal.add(value => {
                latest[i] = value;
                combinedSignal.emit(latest);
            }),
        );
    };

    const detach = () => {
        for (const detach of detachCombinedSignals) {
            detach();
        }
    };

    const combinedSignal = createSignal<ExtractSignalsState<T>>({
        describeSubject: () => ({sources: signals.map(signal => signal.name)}),
    });
    const combinedSignalProxy = new Proxy(combinedSignal, {
        get: (target, p: keyof Signal<ExtractSignalsState<T>>, args) => {
            switch (p) {
                case 'add':
                    return (
                        ...args: Parameters<
                            Signal<ExtractSignalsState<T>>['add']
                        >
                    ) => {
                        const value = target[p](...args);
                        if (
                            detachCombinedSignals.length === 0 &&
                            combinedSignal.size > 0
                        ) {
                            attach();
                        }
                        return value;
                    };

                case 'remove':
                    return (
                        ...args: Parameters<
                            Signal<ExtractSignalsState<T>>['remove']
                        >
                    ) => {
                        const value = target[p](...args);
                        if (combinedSignal.size === 0) {
                            detach();
                        }
                        return value;
                    };

                default:
                    return Reflect.get(target, p, args);
            }
        },
    });

    return combinedSignalProxy;
};
